// src/services/chat.js

const API_BASE_URL = `${import.meta.env.VITE_API_URL}/api`;
const API_KEY = import.meta.env.VITE_API_KEY;

// ==========================================
// SHARED - Credits
// ==========================================

/**
 * Get user's credits info
 * @param {string} token - JWT authentication token
 * @returns {Promise<Object>} - { credits: number, questions_asked?: number }
 */
export async function getCreditsInfo(token) {
  const response = await fetch(`${API_BASE_URL}/chat/credits`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

// ==========================================
// PATIENT - Single Questions (No History)
// ==========================================

/**
 * Ask a question as a patient (no conversation history)
 * @param {string} token - JWT authentication token
 * @param {string} question - The question to ask
 * @param {string} language - Language preference (default: 'english')
 * @returns {Promise<Object>} - { answer, credits_remaining, disclaimer }
 */
export async function askPatientQuestion(token, question, language = 'english') {
  const response = await fetch(`${API_BASE_URL}/chat/patient/ask`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
    body: JSON.stringify({
      question,
      language
    })
  });

  if (!response.ok) {
    if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to process question');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

// ==========================================
// PROVIDER - Conversations with History
// ==========================================

/**
 * Create a new conversation
 * @param {string} token - JWT authentication token
 * @param {string} title - Optional conversation title
 * @param {string} language - Language preference (default: 'english')
 * @returns {Promise<Object>} - Conversation object
 */
export async function createConversation(token, title = null, language = 'english') {
  const response = await fetch(`${API_BASE_URL}/chat/provider/conversations`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
    body: JSON.stringify({
      title,
      language
    })
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * List all conversations for the provider
 * @param {string} token - JWT authentication token
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Items per page (default: 10)
 * @returns {Promise<Object>} - { conversations, pagination }
 */
export async function listConversations(token, page = 1, pageSize = 10) {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());

  const response = await fetch(`${API_BASE_URL}/chat/provider/conversations?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * Get a conversation with all messages
 * @param {string} token - JWT authentication token
 * @param {string} conversationId - UUID of the conversation
 * @returns {Promise<Object>} - { conversation, messages }
 */
export async function getConversation(token, conversationId) {
  const response = await fetch(`${API_BASE_URL}/chat/provider/conversations/${conversationId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Conversation not found');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * Send a message in a conversation
 * @param {string} token - JWT authentication token
 * @param {string} conversationId - UUID of the conversation
 * @param {string} message - The message to send
 * @returns {Promise<Object>} - { message, credits_remaining }
 */
export async function sendMessage(token, conversationId, message) {
  const response = await fetch(`${API_BASE_URL}/chat/provider/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
    body: JSON.stringify({
      message
    })
  });

  if (!response.ok) {
    if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send message');
    }
    if (response.status === 404) {
      throw new Error('Conversation not found');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * Delete a conversation
 * @param {string} token - JWT authentication token
 * @param {string} conversationId - UUID of the conversation
 * @returns {Promise<void>}
 */
export async function deleteConversation(token, conversationId) {
  const response = await fetch(`${API_BASE_URL}/chat/provider/conversations/${conversationId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Conversation not found');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return;
}

// ==========================================
// HELPER - Unified chat function based on role
// ==========================================

/**
 * Send a chat message based on user role
 * @param {string} token - JWT authentication token
 * @param {string} role - User role ('patient' or 'provider')
 * @param {string} message - The message to send
 * @param {Object} options - Additional options
 * @param {string} options.conversationId - Required for provider role
 * @param {string} options.language - Language preference
 * @returns {Promise<Object>} - Response with answer/message and credits_remaining
 */
export async function sendChatMessage(token, role, message, options = {}) {
  const { conversationId, language = 'english' } = options;

  if (role === 'patient') {
    return await askPatientQuestion(token, message, language);
  } else if (role === 'provider') {
    if (!conversationId) {
      throw new Error('Conversation ID required for provider chat');
    }
    return await sendMessage(token, conversationId, message);
  } else {
    throw new Error(`Unknown role: ${role}`);
  }
}